import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Newspaper, Sprout, Bug, Sparkles, DollarSign, Leaf, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Digest({ user }) {
  const [digest, setDigest] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/digest/current`, { withCredentials: true, timeout: 120000 })
      .then(r => setDigest(r.data))
      .catch(() => setError(true));
    axios.post(`${API}/digest/dismiss`, {}, { withCredentials: true }).catch(() => {});
  }, []);

  if (error) return (
    <Layout user={user}>
      <p className="text-[#C66B4A] p-6">Couldn&apos;t load this month&apos;s digest — please try again shortly.</p>
    </Layout>
  );

  if (!digest) return (
    <Layout user={user}>
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        <p className="text-sm text-[#7A6B57] italic">Writing your personalised digest…</p>
      </div>
    </Layout>
  );

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Masthead */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#2E5C46] rounded-3xl p-8" data-testid="digest-masthead">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#C9A85B] flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> The Grower&apos;s Digest · {digest.month_label}
            {digest.is_season_prep && <span className="bg-[#C66B4A] text-[#FAF6EC] px-2 py-0.5 rounded-full text-[10px]">SEASON PREP: {digest.prep_for?.toUpperCase()}</span>}
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#FAF6EC] tracking-tight mt-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="digest-title">
            {digest.title}
          </h1>
          <p className="text-sm text-[#FAF6EC]/85 mt-3 leading-relaxed">{digest.intro}</p>
          <p className="text-xs text-[#FAF6EC]/60 mt-3">{digest.region} · {digest.season} edition · written for {user?.name?.split(' ')[0]}</p>
        </div>

        {/* Month value recap */}
        {digest.value_recap?.harvests > 0 && (
          <div className="bg-[#C9A85B]/10 border border-[#C9A85B]/30 rounded-3xl p-5 flex items-center gap-4" data-testid="digest-value-recap">
            <div className="w-12 h-12 bg-[#C9A85B]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-[#C9A85B]" />
            </div>
            <div>
              <p className="text-lg font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ${Math.round(digest.value_recap.low)}–${Math.round(digest.value_recap.high)} grown last month
              </p>
              <p className="text-xs text-[#7A6B57]">{digest.value_recap.harvests} harvest{digest.value_recap.harvests === 1 ? '' : 's'} — keep it up</p>
            </div>
          </div>
        )}

        {/* Sow now */}
        <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="digest-sow-now">
          <h2 className="text-2xl font-semibold text-[#1A4331] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Sprout className="w-6 h-6 text-[#6B9F4A]" /> Sow this month
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {digest.sow_now.map((s, i) => (
              <div key={i} className="bg-white/70 rounded-2xl p-4">
                <p className="font-semibold text-[#1A4331]">{s.crop}</p>
                <p className="text-sm text-[#2A3528] mt-1 leading-relaxed">{s.why}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Crop care */}
        {digest.crop_care?.length > 0 && (
          <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="digest-crop-care">
            <h2 className="text-2xl font-semibold text-[#1A4331] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Leaf className="w-6 h-6 text-[#6B9F4A]" /> Your crops this month
            </h2>
            <ul className="space-y-3">
              {digest.crop_care.map((c, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-[#6B9F4A] mt-2 flex-shrink-0"></span>
                  <p className="text-sm text-[#2A3528] leading-relaxed"><span className="font-semibold text-[#1A4331]">{c.crop}:</span> {c.tip}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pest watch */}
        {digest.pest_watch?.name && (
          <div className="bg-[#C66B4A]/8 border border-[#C66B4A]/25 rounded-3xl p-6" data-testid="digest-pest-watch">
            <h2 className="text-2xl font-semibold text-[#1A4331] mb-2 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Bug className="w-6 h-6 text-[#C66B4A]" /> Pest watch: {digest.pest_watch.name}
            </h2>
            <p className="text-sm text-[#2A3528] leading-relaxed">{digest.pest_watch.what_to_do}</p>
          </div>
        )}

        {/* Season prep */}
        {digest.season_prep?.length > 0 && (
          <div className="bg-[#E5EBDD] rounded-3xl p-6" data-testid="digest-season-prep">
            <h2 className="text-2xl font-semibold text-[#1A4331] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sparkles className="w-6 h-6 text-[#C9A85B]" /> {digest.is_season_prep ? `Get ready for ${digest.prep_for}` : 'Prep for the weeks ahead'}
            </h2>
            <ul className="space-y-2">
              {digest.season_prep.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#2A3528]">
                  <span className="w-5 h-5 rounded-full bg-[#1A4331] text-[#FAF6EC] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-center text-sm text-[#7A6B57] italic px-6">{digest.closing}</p>

        <Link to="/coach" data-testid="digest-ask-coach" className="block bg-[#FAF6EC] rounded-3xl p-5 border border-[#1A4331]/10 card-hover">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[#1A4331]">Questions about anything in this digest? Ask your AI Coach</p>
            <ChevronRight className="w-4 h-4 text-[#7A6B57]" />
          </div>
        </Link>
      </div>
    </Layout>
  );
}

export function DigestBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    axios.get(`${API}/digest/should-show`, { withCredentials: true })
      .then(r => setShow(r.data.should_show))
      .catch(() => {});
  }, []);
  if (!show) return null;
  return (
    <Link to="/digest" data-testid="digest-banner"
      className="w-full bg-gradient-to-br from-[#C9A85B] to-[#A8863D] rounded-3xl p-5 flex items-center gap-4 text-left hover:scale-[1.01] transition-transform block">
      <div className="w-12 h-12 bg-[#1A4331]/20 rounded-full flex items-center justify-center flex-shrink-0">
        <Newspaper className="w-6 h-6 text-[#1A4331]" />
      </div>
      <div className="flex-1">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1A4331]/70">New this month</p>
        <p className="text-lg font-semibold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Your Grower&apos;s Digest is ready — what to sow, watch & prep
        </p>
      </div>
      <ChevronRight className="w-5 h-5 text-[#1A4331]/60" />
    </Link>
  );
}
